// src/components/Brand.tsx
// Single source of truth for the Miraal.AI icon + wordmark - used by the
// sidebar header (App.tsx) and the auth page (AuthPage.tsx), which
// previously each carried their own duplicate logo implementation.
import React from 'react';

const SPACE_GROTESK = "'Space Grotesk', 'Plus Jakarta Sans', sans-serif";

export const MiraalIcon: React.FC<{
  size?: number;
  variant?: 'dark' | 'light';
  className?: string;
}> = ({ size = 32, variant = 'dark', className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 34 34"
    className={className}
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="6" cy="6" r="3.2" fill="#8B5CF6" />
    <circle cx="28" cy="6" r="3.2" fill="#8B5CF6" />
    <circle cx="6" cy="28" r="3.2" fill="#8B5CF6" />
    <circle cx="17" cy="17" r="4" fill={variant === 'light' ? '#111111' : '#ECECEF'} />
    <line x1="8.2" y1="7.8" x2="14.2" y2="14.5" stroke="#6D28D9" strokeWidth="1.6" />
    <line x1="25.8" y1="7.8" x2="19.8" y2="14.5" stroke="#6D28D9" strokeWidth="1.6" />
    <line x1="8.2" y1="26.2" x2="14.2" y2="19.5" stroke="#6D28D9" strokeWidth="1.6" />
  </svg>
);

export const MiraalWordmark: React.FC<{
  size?: number;
  variant?: 'dark' | 'light';
  className?: string;
}> = ({ size = 20, variant = 'dark', className }) => {
  const suffixColor = variant === 'light' ? '#6D28D9' : '#8B5CF6';
  const textColor = variant === 'light' ? '#111111' : '#ffffff';
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        fontFamily: SPACE_GROTESK,
        fontWeight: 600,
        fontSize: size,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        color: textColor,
      }}
    >
      <span style={{ display: 'inline-block' }}>Mir</span>
      {/* the arc's width is tied to this wrapper's own rendered width (100%),
          not a fixed px guess, so it always sits over exactly "aa" instead
          of drifting toward "M" or "l" at different font sizes */}
      <span style={{ position: 'relative', display: 'inline-block' }}>
        <span style={{ display: 'inline-block' }}>aa</span>
        <svg
          viewBox="0 0 30 8"
          preserveAspectRatio="none"
          style={{ position: 'absolute', top: -size * 0.5, left: 0, width: '100%', height: size * 0.4, overflow: 'visible', pointerEvents: 'none' }}
          aria-hidden="true"
        >
          {/* the curve's peak (y=-2) sits above the viewBox's own top edge
              (y=0) - without overflow:visible the SVG clips it and the arc
              never actually shows */}
          <path d="M2 6 Q15 -2 28 6" stroke={suffixColor} strokeWidth="1.6" fill="none" vectorEffect="non-scaling-stroke" />
        </svg>
      </span>
      <span style={{ display: 'inline-block' }}>l</span>
      <span style={{ fontWeight: 500, color: suffixColor }}>.AI</span>
    </span>
  );
};
